import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
    LayoutDashboard, FileText, Users, Truck, ShoppingCart,
    Package, Receipt, CreditCard, BarChart3, Settings,
    FolderTree, ClipboardList, FileCheck, Briefcase, X, Building2, Shield
} from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import './Sidebar.css';

const menuItems = [
    {
        section: 'Main',
        items: [
            { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard.view' }
        ]
    },
    {
        section: 'Procurement',
        items: [
            { path: '/purchase-requests', icon: FileText, label: 'Purchase Requests', permission: 'pr.view' },
            { path: '/rfq', icon: ClipboardList, label: 'RFQ', permission: 'rfq.view' },
            { path: '/contracts', icon: FileCheck, label: 'Contracts', permission: 'contract.view' },
            { path: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders', permission: 'po.view' }
        ]
    },
    {
        section: 'Operations',
        items: [
            { path: '/goods-receipts', icon: Package, label: 'Goods Receipt', permission: 'gr.view' },
            { path: '/invoices', icon: Receipt, label: 'Invoices', permission: 'invoice.view' },
            { path: '/payments', icon: CreditCard, label: 'Payments', permission: 'payment.view' }
        ]
    },
    {
        section: 'Master Data',
        items: [
            { path: '/departments', icon: Building2, label: 'Departments', permission: 'department.view' },
            { path: '/vendors', icon: Truck, label: 'Vendors', permission: 'vendor.view' },
            { path: '/catalog', icon: FolderTree, label: 'Catalog', permission: 'catalog.view' },
            { path: '/budgets', icon: Briefcase, label: 'Budgets', permission: 'budget.view' }
        ]
    },
    {
        section: 'Analytics',
        items: [
            { path: '/reports', icon: BarChart3, label: 'Reports', permission: 'report.view' }
        ]
    },
    {
        section: 'Administration',
        items: [
            { path: '/users', icon: Users, label: 'Users', permission: 'user.view' },
            { path: '/roles', icon: Shield, label: 'Roles', permission: 'role.view' },
            { path: '/settings', icon: Settings, label: 'Settings', permission: 'system.settings' }
        ]
    }
];

export default function Sidebar({ isOpen, onClose, collapsed }) {
    const { hasPermission } = useAuthStore();
    const location = useLocation();

    const filteredMenu = menuItems.map(section => ({
        ...section,
        items: section.items.filter(item => !item.permission || hasPermission(item.permission))
    })).filter(section => section.items.length > 0);

    return (
        <aside className={`sidebar ${isOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
                <div className="sidebar-brand">
                    <div className="sidebar-logo">EP</div>
                    <div className="sidebar-title">
                        <span className="sidebar-name">E-Procurement</span>
                        <span className="sidebar-subtitle">Management System</span>
                    </div>
                </div>
                <button className="sidebar-close" onClick={onClose}>
                    <X size={20} />
                </button>
            </div>

            <nav className="sidebar-nav">
                {filteredMenu.map((section, idx) => (
                    <div key={idx} className="nav-section">
                        <div className="nav-section-title">{section.section}</div>
                        {section.items.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) =>
                                    `nav-item ${isActive || (item.path !== '/' && location.pathname.startsWith(item.path)) ? 'active' : ''}`
                                }
                                end={item.path === '/'}
                            >
                                <item.icon size={20} />
                                <span>{item.label}</span>
                            </NavLink>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="sidebar-footer">
                <div className="sidebar-version">v1.0.0</div>
            </div>
        </aside>
    );
}

Sidebar.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    collapsed: PropTypes.bool
};
